import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';

import { M } from '@/business';

/** Branches people work from. Values are stored as written; labels come from `admin:branches.<key>`. */
export const BRANCHES = [
  { value: 'Dubai', key: 'dubai' },
  { value: 'Abu Dhabi', key: 'abuDhabi' },
  { value: 'Sharjah', key: 'sharjah' },
  { value: 'Ajman', key: 'ajman' },
  { value: 'Ras Al Khaimah', key: 'rak' },
  { value: 'Fujairah', key: 'fujairah' },
  { value: 'Umm Al Quwain', key: 'uaq' },
] as const;
export const branchKey = (value: string) => BRANCHES.find((b) => b.value === value)?.key ?? null;

export const EMPLOYMENT_TYPES = ['employee', 'outsourced'] as const satisfies readonly Schema<'EmploymentType'>[];

const name = z.string({ error: M.required }).trim().min(2, M.required);
const branch = z.string({ error: M.required }).min(1, M.required);
const employmentType = z.enum(EMPLOYMENT_TYPES);

export const inviteSchema = z.object({
  name,
  email: z.string({ error: M.required }).trim().min(1, M.required).pipe(z.email({ error: M.email })),
  roleId: z.string({ error: M.required }).min(1, M.required),
  /** '' = no team (operations, finance, management). */
  teamId: z.string(),
  branch,
  employmentType,
});
export type InviteValues = z.infer<typeof inviteSchema>;
export const inviteDefaults: InviteValues = { name: '', email: '', roleId: '', teamId: '', branch: 'Dubai', employmentType: 'employee' };
export const toInviteInput = (v: InviteValues): Schema<'AdminUserInvite'> => ({ name: v.name.trim(), email: v.email.trim().toLowerCase(), roleId: v.roleId, teamId: v.teamId || null, branch: v.branch, employmentType: v.employmentType });

export const editUserSchema = z.object({ name, branch, employmentType });
export type EditUserValues = z.infer<typeof editUserSchema>;
export const toEditUserValues = (u: Schema<'AdminUser'>): EditUserValues => ({ name: u.name, branch: u.branch, employmentType: u.employmentType ?? 'employee' });
export const toEditUserInput = (v: EditUserValues): Schema<'AdminUserUpdate'> => ({ name: v.name.trim(), branch: v.branch, employmentType: v.employmentType });

export const changeRoleSchema = z.object({ roleId: z.string({ error: M.required }).min(1, M.required) });
export type ChangeRoleValues = z.infer<typeof changeRoleSchema>;

// ---------------------------------------------------------------- employee details (A2)

/** Internal employee number (unique per organisation; the server checks uniqueness). Empty = none. */
export const employeeNumberSchema = z.object({ employeeNumber: z.string().trim().regex(/^([A-Za-z0-9][A-Za-z0-9-]{0,19})?$/, 'validation.employeeNumber') });
export type EmployeeNumberValues = z.infer<typeof employeeNumberSchema>;
export const toEmployeeNumber = (v: EmployeeNumberValues): string | null => v.employeeNumber.trim() || null;

/** A code a bank issued to the user (DSA / agent code). */
export const bankCodeSchema = z.object({
  bankId: z.string({ error: M.required }).min(1, M.required),
  code: z.string({ error: M.required }).trim().min(1, M.required).regex(/^[A-Za-z0-9][A-Za-z0-9/_-]{0,29}$/, 'validation.bankCode'),
  effectiveFrom: z.string({ error: M.required }).regex(/^\d{4}-\d{2}-\d{2}$/, M.date),
  note: z.string().max(300).optional(),
});
export type BankCodeValues = z.infer<typeof bankCodeSchema>;
export const toBankCodeInput = (v: BankCodeValues): Schema<'EmployeeBankCodeInput'> => ({ bankId: v.bankId, code: v.code.trim(), effectiveFrom: v.effectiveFrom, ...(v.note?.trim() ? { note: v.note.trim() } : {}) });

export const endBankCodeSchema = z.object({ effectiveTo: z.string({ error: M.required }).regex(/^\d{4}-\d{2}-\d{2}$/, M.date), note: z.string().max(300).optional() });
export type EndBankCodeValues = z.infer<typeof endBankCodeSchema>;
